import type {
  ConnectorFolder,
  ConnectorItem,
  KnowledgeBase,
  Locale,
  PreviewSummary,
  UpdateSelectionRequest
} from '@ralph/contracts'
import clsx from 'clsx'
import { CircleCheck, TriangleAlert } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Input, Spinner } from '@/components/ui/primitives.tsx'
import {
  useApplySelection,
  useCheckNow,
  useConnectorAvailability,
  useFolder,
  useItems,
  usePreview,
  usePreviewSelection,
  useViewer
} from '@/hooks/use-connectors.ts'
import { describeApiError, useI18n } from '@/i18n/index.ts'
import { folderAckText, problemSentence, yearOf } from './connected-folders.ts'
import { FolderAudienceBlock } from './FolderAudienceBlock.tsx'
import { type PendingRule, SelectionTable } from './SelectionTable.tsx'

/**
 * What will be loaded (PRD-25 D-15, D-16): the heart of the connect flow, and the same
 * instrument the folder detail reuses to change what is included afterwards.
 *
 * The summary is the knowledge base card's own grammar — one sentence with a glyph, a
 * segmented bar, a row of pressable legend chips — so a maintainer who has read that card once
 * reads this the same way. The count streams: the sentence and the bar redraw as pages land, Start is
 * enabled once the first page has, and the sentence above the button says which numbers
 * are still moving. Below the summary: the type line, the grouped disclosures for what is
 * left out, three filter chips that state their consequence, the selection table, and the
 * access block that the Start button stands on.
 */

type Filters = { extensions: string[] | null; maxBytes: number | null; modifiedAfter: string | null }

/** What the instrument holds unsaved, and the one call that saves it. */
export type PendingState = {
  added: number | null
  withdrawn: number | null
  apply: () => Promise<boolean>
}

export const FolderPreview = ({
  folder: initial,
  base,
  mode,
  readOnly = false,
  onStarted,
  onCancel,
  pendingRules,
  setPendingRules,
  onApplied,
  onPending
}: {
  folder: ConnectorFolder
  base: Pick<KnowledgeBase, 'id' | 'name' | 'access' | 'owner' | 'lentToCount'>
  /** `connect` shows the access block and Start; `edit` shows the pending-changes bar and Apply. */
  mode: 'connect' | 'edit'
  /**
   * What is included can be read but not changed: the folder reads through an account this viewer
   * may not use (PRD-26 D-38), so the server would refuse the change.
   */
  readOnly?: boolean
  /**
   * The acknowledgement, with the language it was read in (PRD-27 D-7). The folder row already
   * records who acknowledged, when, and against which access, so the language is the one thing
   * that was missing from it.
   */
  onStarted?: (ack: { ack: boolean; ackText: string; ackLocale: Locale }) => void
  onCancel?: () => void
  pendingRules: Map<string, PendingRule>
  setPendingRules: (next: Map<string, PendingRule>) => void
  onApplied?: () => void
  /** What is unsaved, for a container that has to ask before closing; null when nothing is. */
  onPending?: (pending: PendingState | null) => void
}) => {
  // Bound inside the body, never at module scope (PRD-27 F-3).
  const fmt = useI18n()
  const t = fmt.t.knowledgeFolders
  // The folder the caller holds may be the snapshot Create returned; the count lives on the
  // live row, which the hook polls while a walk is running.
  const live = useFolder(initial.id)
  const folder = live.data ?? initial
  const counting = folder.status === 'previewing' && (folder.syncing || folder.lastSyncAt === null)
  const preview = usePreview(folder.id, counting)
  const summary = preview.data
  const [parent, setParent] = useState<string | null>(null)
  const [filters, setFilters] = useState<Filters>({
    extensions: folder.filters.extensions,
    maxBytes: folder.filters.maxBytes,
    modifiedAfter: folder.filters.modifiedAfter
  })
  const [acknowledged, setAcknowledged] = useState(false)
  const [legendFilter, setLegendFilter] = useState<'unsupported' | 'too_large' | 'filtered' | 'left_out' | null>(null)

  const request: UpdateSelectionRequest = useMemo(
    () => ({
      rules: [...pendingRules.values()].map((rule) => ({ remoteId: rule.remoteId, effect: rule.effect })),
      filters: {
        extensions: filters.extensions,
        maxBytes: filters.maxBytes,
        modifiedAfter: filters.modifiedAfter
      }
    }),
    [pendingRules, filters]
  )
  const dirty =
    pendingRules.size > 0 ||
    JSON.stringify(filters) !==
      JSON.stringify({
        extensions: folder.filters.extensions,
        maxBytes: folder.filters.maxBytes,
        modifiedAfter: folder.filters.modifiedAfter
      })

  // The consequence of what is pending, from the server: the sentence before Apply is a real
  // count, never a client-side estimate.
  const diff = usePreviewSelection()
  const previewDiff = diff.mutate
  useEffect(() => {
    if (!dirty) return
    const timer = setTimeout(() => previewDiff({ id: folder.id, request }), 250)
    return () => clearTimeout(timer)
  }, [dirty, folder.id, request, previewDiff])

  const apply = useApplySelection()
  const applyMutate = apply.mutate
  const onAppliedRef = useRef(onApplied)
  onAppliedRef.current = onApplied
  const onPendingRef = useRef(onPending)
  onPendingRef.current = onPending
  const applyPending = useCallback(
    () =>
      new Promise<boolean>((resolve) =>
        applyMutate(
          { id: folder.id, request },
          {
            onSuccess: () => {
              setPendingRules(new Map())
              onAppliedRef.current?.()
              resolve(true)
            },
            onError: () => resolve(false)
          }
        )
      ),
    [applyMutate, folder.id, request, setPendingRules]
  )
  const applyNow = () => void applyPending()
  const diffAdded = diff.data?.added ?? null
  const diffWithdrawn = diff.data?.withdrawn ?? null
  useEffect(() => {
    onPendingRef.current?.(dirty ? { added: diffAdded, withdrawn: diffWithdrawn, apply: applyPending } : null)
  }, [dirty, diffAdded, diffWithdrawn, applyPending])
  useEffect(() => () => onPendingRef.current?.(null), [])

  const availability = useConnectorAvailability()
  const uploadMax = availability.data?.uploadMaxBytes ?? 25 * 1024 * 1024
  const maxItems = availability.data?.maxItemsPerFolder ?? 50_000
  const check = useCheckNow()
  const viewer = useViewer()

  const stats = summary?.stats ?? folder.stats
  const total = stats.files
  // Two different numbers, kept apart: what the readers can open, and what the selection
  // includes. "Can be read" is the first; "will be loaded" is the second.
  const readable = Math.max(0, stats.files - stats.unsupported - stats.tooLarge)
  const included = stats.included
  const everyone = base.access.kind === 'everyone'
  const canStart = !counting || stats.listed > 1
  const needsAck = everyone || folder.audience.stale || !folder.audience.acknowledged
  const startBlocked = !canStart || (included === 0 && !counting) || (needsAck && everyone && !acknowledged)
  const countFailed = folder.status === 'previewing' && !counting && folder.lastError !== null
  const countProblem = countFailed ? problemSentence(fmt, folder) : null

  const toggle = (item: ConnectorItem, effect: 'include' | 'exclude' | null) => {
    const next = new Map(pendingRules)
    // Toggling back to what the server already holds — its explicit rule, or the state it
    // has with no rule at all — clears the pending rule rather than stacking one.
    // `removed_by_steward` is the server's code for a file a maintainer removed by hand.
    const filteredOut = item.reason === 'rule' || item.reason === 'not_selected' || item.reason === 'removed_by_steward'
    const serverEffect: 'include' | 'exclude' | null =
      item.rule ??
      (item.kind === 'file'
        ? item.decision === 'included' || !filteredOut
          ? 'include'
          : 'exclude'
        : item.effective === 'all'
          ? 'include'
          : item.effective === 'none'
            ? 'exclude'
            : null)
    if (effect === serverEffect) next.delete(item.remoteId)
    else next.set(item.remoteId, { remoteId: item.remoteId, effect })
    setPendingRules(next)
  }

  const otherKinds = summary ? summary.byExtension.slice(6) : []
  const otherCount = otherKinds.reduce((sum, kind) => sum + kind.count, 0)

  return (
    <div className="space-y-4">
      {/* The sentence, the bar, the chips — the knowledge base card's own instrument. */}
      <div>
        <p role="status" aria-live="polite" className="flex items-start gap-1.5 text-pretty text-[13px] leading-[1.45]">
          {counting ? (
            <>
              <Spinner size={12} className="mt-[3px] shrink-0" />
              <span>{t.preview.counting(fmt, stats.files)}</span>
            </>
          ) : total === 0 ? (
            <>
              <CircleCheck size={12} strokeWidth={2.3} className="mt-[3px] shrink-0 text-fg-subtle" aria-hidden />
              <span>{t.preview.empty}</span>
            </>
          ) : countFailed && countProblem ? (
            <>
              <TriangleAlert size={12} strokeWidth={2.3} className="mt-[3px] shrink-0 text-alert" aria-hidden />
              <span>
                <span className="font-semibold">{countProblem.title}</span> {countProblem.body}
              </span>
            </>
          ) : readable === 0 ? (
            <>
              <TriangleAlert size={12} strokeWidth={2.3} className="mt-[3px] shrink-0 text-alert" aria-hidden />
              <span>{t.preview.nothingReadable(fmt, total, base.name)}</span>
            </>
          ) : included === 0 ? (
            <>
              <TriangleAlert size={12} strokeWidth={2.3} className="mt-[3px] shrink-0 text-alert" aria-hidden />
              <span>{t.preview.nothingIncluded}</span>
            </>
          ) : (
            <>
              {readable < total ? (
                <TriangleAlert size={12} strokeWidth={2.3} className="mt-[3px] shrink-0 text-alert" aria-hidden />
              ) : (
                <CircleCheck size={12} strokeWidth={2.3} className="mt-[3px] shrink-0 text-grounded" aria-hidden />
              )}
              <span>
                {t.preview.summary(
                  fmt,
                  readable,
                  total,
                  fmt.formatBytes(stats.includedBytes),
                  Math.max(0, stats.folders - 1)
                )}
              </span>
            </>
          )}
        </p>
        {countFailed && (
          <div className="mt-2">
            <Button
              size="sm"
              variant="primary"
              disabled={check.isPending}
              onClick={() => check.mutate({ id: folder.id, walk: true })}
            >
              {t.standing.countAgain}
            </Button>
          </div>
        )}

        {total > 0 && included < total && (
          <>
            <div className="mt-2.5 flex h-[6px] gap-[2px] overflow-hidden rounded-full" aria-hidden>
              {included > 0 && <div className="rounded-full bg-grounded" style={{ flexGrow: included }} />}
              {stats.unsupported > 0 && (
                <div className="rounded-full bg-alert/45" style={{ flexGrow: stats.unsupported }} />
              )}
              {stats.tooLarge > 0 && <div className="rounded-full bg-alert" style={{ flexGrow: stats.tooLarge }} />}
              {stats.filtered > 0 && <div className="rounded-full bg-fg/25" style={{ flexGrow: stats.filtered }} />}
              {stats.leftOut > 0 && <div className="rounded-full bg-fg/15" style={{ flexGrow: stats.leftOut }} />}
            </div>
            <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1.5">
              {(
                [
                  ['included', included, 'bg-grounded', null],
                  ['unsupported', stats.unsupported, 'bg-alert/45', 'unsupported'],
                  ['tooLarge', stats.tooLarge, 'bg-alert', 'too_large'],
                  ['filtered', stats.filtered, 'bg-fg/25', 'filtered'],
                  ['leftOut', stats.leftOut, 'bg-fg/15', 'left_out']
                ] as const
              )
                .filter(([, count]) => count > 0)
                .map(([key, count, dot, filterKey]) => {
                  const active = legendFilter !== null && legendFilter === filterKey
                  return (
                    <button
                      key={key}
                      type="button"
                      aria-pressed={active}
                      disabled={filterKey === null}
                      onClick={() => setLegendFilter(active ? null : filterKey)}
                      className={clsx(
                        'flex items-center gap-1.5 rounded-full px-1.5 py-0.5 text-[11.5px] tracking-[-0.01em] transition-colors duration-200',
                        active ? 'bg-card-solid font-semibold text-fg' : 'text-fg-muted enabled:hover:text-fg',
                        filterKey === null && 'cursor-default'
                      )}
                    >
                      <span className={clsx('h-[7px] w-[7px] shrink-0 rounded-full', dot)} aria-hidden />
                      {/* The chip's whole phrase, count included: the number governs the
                          agreement of the words beside it in Portuguese (D-21). */}
                      {t.preview.legend[key](fmt, count)}
                    </button>
                  )
                })}
            </div>
          </>
        )}

        {summary && summary.byExtension.length > 0 && (
          <p className="readout mt-2 text-fg-muted">
            {summary.byExtension
              .slice(0, 6)
              .map((kind) => `${kind.extension.replace(/^\./, '')} ${fmt.formatCount(kind.count)}`)
              .join(' · ')}
            {otherCount > 0 ? ` · ${t.preview.otherKinds(fmt, otherCount, otherKinds.length)}` : ''}
          </p>
        )}

        {summary?.truncated && (
          <p className="mt-2 flex items-start gap-1.5 text-pretty text-[12.5px] leading-[1.45] text-alert">
            <TriangleAlert size={12} strokeWidth={2.2} className="mt-[3px] shrink-0" aria-hidden />
            {t.preview.truncated(fmt, maxItems)}
          </p>
        )}
      </div>

      {/* Grouped by reason with a count, never a flat list with a button per row. */}
      {summary && (stats.unsupported > 0 || stats.tooLarge > 0 || stats.unknownSize > 0) && (
        <div className="space-y-1.5">
          {stats.unsupported > 0 && (
            <Disclosure
              heading={t.preview.unsupportedHeading(fmt, stats.unsupported)}
              // `unsupportedByKind` carries a free-text label the SERVER wrote ("a Word file")
              // and no code to key a label map off, so the sentence that counts those labels
              // has to be built per locale. It is a catalogue entry rather than a template
              // here, which is what lets English keep reshaping the label and Portuguese
              // refuse to (LOC-014). The real fix is a kind code on `PreviewSummary`.
              body={t.preview.unsupportedDetail(summary.unsupportedByKind.slice(0, 3))}
              folderId={folder.id}
              filter="unsupported"
            />
          )}
          {stats.tooLarge > 0 && (
            <Disclosure
              heading={t.preview.tooLargeHeading(fmt, stats.tooLarge)}
              body={t.preview.tooLargeBody(
                fmt.formatBytes(uploadMax),
                summary.largestBytes ? fmt.formatBytes(summary.largestBytes) : null
              )}
              folderId={folder.id}
              filter="too_large"
            />
          )}
          {stats.unknownSize > 0 && (
            <p className="text-[12.5px] leading-[1.45] text-fg-muted">
              {t.preview.unknownSize(fmt, stats.unknownSize)}
            </p>
          )}
        </div>
      )}

      {/* Three chips that state their consequence, expanding in flow — not offered where the
          change would be refused. */}
      {!readOnly && (
        <FilterChips summary={summary ?? null} filters={filters} onChange={setFilters} uploadMax={uploadMax} />
      )}

      {legendFilter ? (
        <FilteredList folderId={folder.id} filter={legendFilter} onClose={() => setLegendFilter(null)} />
      ) : (
        <div>
          <span className="eyebrow mb-1.5 block text-fg-muted">{t.selection.title}</span>
          <a
            href="#folder-consequence"
            className="sr-only focus:not-sr-only focus:block focus:py-1 focus:text-[12.5px]"
          >
            {t.selection.skipList}
          </a>
          <SelectionTable
            folderId={folder.id}
            rootName={folder.displayPath.split(' / ').pop() ?? folder.displayPath}
            pending={pendingRules}
            onToggle={toggle}
            readOnly={readOnly}
            refresh={counting}
            parent={parent}
            onNavigate={setParent}
          />
        </div>
      )}

      {mode === 'connect' ? (
        <div id="folder-consequence" className="space-y-3 border-t border-rule pt-3">
          <p className="text-pretty text-[13px] leading-[1.5]">
            {counting ? (
              <span className="font-semibold">{t.preview.willLoadCounting(fmt, stats.files)}</span>
            ) : (
              <span className="font-semibold">
                {t.preview.willLoad(fmt, included, fmt.formatBytes(stats.includedBytes), total - included)}
              </span>
            )}
          </p>
          {dirty && diff.data && diff.data.added > 0 && (
            <p className="text-[12.5px] leading-[1.45] text-fg-muted" role="status">
              {t.selection.pending.addedOnly(fmt, diff.data.added)}
            </p>
          )}
          <FolderAudienceBlock
            base={base}
            surface={folder.surface}
            acknowledged={acknowledged}
            onAcknowledge={setAcknowledged}
            stale={folder.audience.stale}
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="primary"
              disabled={startBlocked || apply.isPending}
              title={
                included === 0 && !counting
                  ? readable === 0
                    ? t.preview.startDisabledEmpty
                    : t.preview.startDisabledNothingIncluded
                  : undefined
              }
              onClick={async () => {
                // A selection that did not apply is not started over: the folder would load
                // exactly what the maintainer just left out.
                if (dirty && !(await applyPending())) return
                onStarted?.({ ack: true, ackText: folderAckText(fmt, base, viewer.id), ackLocale: fmt.locale })
              }}
            >
              {t.preview.start}
            </Button>
            {onCancel && (
              <Button variant="ghost" onClick={onCancel}>
                {t.preview.cancel}
              </Button>
            )}
            {included === 0 && !counting && total > 0 && (
              <span className="text-[12.5px] text-fg-subtle">
                {readable === 0 ? t.preview.startDisabledEmpty : t.preview.startDisabledNothingIncluded}
              </span>
            )}
            {apply.isError && (
              <span role="alert" className="readout text-alert">
                {describeApiError(fmt.t, apply.error)}
              </span>
            )}
          </div>
        </div>
      ) : (
        dirty && (
          <section
            aria-label={t.selection.pending.region}
            className="sticky bottom-0 border-t border-rule bg-card-solid px-1 py-3"
          >
            <p className="text-[13px] leading-[1.45]">
              {diff.isPending || !diff.data ? (
                <span className="flex items-center gap-1.5 text-fg-muted">
                  <Spinner size={11} /> {t.selection.pending.working}
                </span>
              ) : diff.data.added === 0 && diff.data.withdrawn === 0 ? (
                <span className="text-fg-muted">{t.selection.pending.nothing}</span>
              ) : diff.data.withdrawn > 0 ? (
                <span className="font-semibold">
                  {t.selection.pending.both(fmt, diff.data.added, diff.data.withdrawn)}
                </span>
              ) : (
                <span className="font-semibold">{t.selection.pending.addedOnly(fmt, diff.data.added)}</span>
              )}
            </p>
            {diff.data && diff.data.withdrawn > 0 && (
              <p className="mt-1 text-[12.5px] leading-[1.45] text-fg-muted">{t.selection.pending.body}</p>
            )}
            <div className="mt-2.5 flex flex-wrap items-center gap-2">
              <Button
                variant={diff.data && diff.data.withdrawn > 0 ? 'danger' : 'primary'}
                size="sm"
                disabled={apply.isPending || diff.isPending}
                aria-label={
                  diff.data
                    ? t.selection.pending.applyLabel(fmt, diff.data.added, diff.data.withdrawn)
                    : t.selection.pending.apply
                }
                onClick={applyNow}
              >
                {apply.isPending ? t.selection.pending.applying : t.selection.pending.apply}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setPendingRules(new Map())} disabled={apply.isPending}>
                {t.selection.pending.discard}
              </Button>
              {apply.isError && (
                <span role="alert" className="readout text-alert">
                  {describeApiError(fmt.t, apply.error)}
                </span>
              )}
            </div>
          </section>
        )
      )}
      {mode === 'edit' && apply.isSuccess && !dirty && (
        <p role="status" className="readout text-fg-subtle">
          {t.selection.pending.applied(fmt, apply.data.applied.added, apply.data.applied.withdrawn)}
        </p>
      )}
    </div>
  )
}

/** A heading with a count, a sentence, and the list behind a disclosure. */
const Disclosure = ({
  heading,
  body,
  folderId,
  filter
}: {
  heading: string
  body: string
  folderId: string
  filter: 'unsupported' | 'too_large'
}) => {
  const t = useI18n().t.knowledgeFolders
  const [open, setOpen] = useState(false)
  return (
    <div className="rounded-tile border border-rule bg-card-solid/55 px-3 py-2.5">
      <p className="text-[13px] font-semibold">{heading}</p>
      <p className="mt-0.5 text-[12.5px] leading-[1.45] text-fg-muted">{body}</p>
      <Button size="sm" variant="ghost" className="mt-1.5" aria-expanded={open} onClick={() => setOpen(!open)}>
        {open ? t.preview.hideThem : t.preview.listThem}
      </Button>
      {open && <FilteredList folderId={folderId} filter={filter} />}
    </div>
  )
}

const FilteredList = ({
  folderId,
  filter,
  onClose
}: {
  folderId: string
  filter: 'unsupported' | 'too_large' | 'filtered' | 'left_out'
  onClose?: () => void
}) => {
  const fmt = useI18n()
  const t = fmt.t.knowledgeFolders
  const page = useItems(folderId, { parent: null, filter, query: '' })
  return (
    <div className="mt-2">
      {onClose && (
        <Button size="sm" variant="ghost" onClick={onClose}>
          {t.preview.backToFolders}
        </Button>
      )}
      {page.isLoading ? (
        <p className="text-[12.5px] text-fg-subtle">{fmt.t.common.loading}</p>
      ) : (
        <ul className="mt-1 max-h-64 space-y-1 overflow-y-auto text-[12.5px]">
          {(page.data?.items ?? []).map((item) => (
            <li key={item.id} className="flex flex-wrap items-baseline gap-x-2">
              <span className="truncate">{item.path || item.name}</span>
              <span className="readout text-fg-subtle">
                {item.size !== null ? fmt.formatBytes(item.size) : ''} {t.reason(item.reason, item.detail)}
              </span>
            </li>
          ))}
          {page.data && page.data.total > page.data.items.length && (
            <li className="readout text-fg-subtle">
              {t.preview.andMore(fmt, page.data.total - page.data.items.length)}
            </li>
          )}
        </ul>
      )}
    </div>
  )
}

/**
 * Three chips in one row, in the mono register. Pressing one expands a block underneath, in
 * flow — no popover: this product has drawers and panels and nothing else. When set, a chip
 * states the consequence, not the setting.
 */
const FilterChips = ({
  summary,
  filters,
  onChange,
  uploadMax
}: {
  summary: PreviewSummary | null
  filters: Filters
  onChange: (next: Filters) => void
  uploadMax: number
}) => {
  const fmt = useI18n()
  const t = fmt.t.knowledgeFolders
  const [open, setOpen] = useState<'types' | 'size' | 'date' | null>(null)
  const present = summary?.byExtension ?? []
  const maxMb = Math.max(1, Math.floor(uploadMax / 1024 / 1024))
  const [sizeMb, setSizeMb] = useState(
    filters.maxBytes ? String(Math.round(filters.maxBytes / 1024 / 1024)) : String(maxMb)
  )
  const [date, setDate] = useState(filters.modifiedAfter ? filters.modifiedAfter.slice(0, 10) : '')

  const chip = (key: 'types' | 'size' | 'date', label: string, set: boolean) => (
    <Button
      key={key}
      size="sm"
      variant="mono"
      aria-expanded={open === key}
      className={clsx(set && 'border-rule-strong text-fg')}
      onClick={() => setOpen(open === key ? null : key)}
    >
      {label}
    </Button>
  )

  const suggestion = summary?.suggestion

  return (
    <div>
      <div className="flex flex-wrap items-center gap-1.5">
        {chip(
          'types',
          filters.extensions ? t.selection.filters.onlyTypes(filters.extensions) : t.selection.filters.allTypes,
          filters.extensions !== null
        )}
        {chip(
          'size',
          filters.maxBytes ? t.selection.filters.upTo(fmt.formatBytes(filters.maxBytes)) : t.selection.filters.anySize,
          filters.maxBytes !== null
        )}
        {chip(
          'date',
          filters.modifiedAfter
            ? t.selection.filters.since(fmt.formatDate(filters.modifiedAfter))
            : t.selection.filters.anyTime,
          filters.modifiedAfter !== null
        )}
      </div>

      {suggestion && !filters.modifiedAfter && (
        <p className="mt-2 flex flex-wrap items-center gap-2 text-[12.5px] leading-[1.45] text-fg-muted">
          {t.preview.suggestion(fmt, suggestion.count, yearOf(suggestion.before))}
          <Button size="sm" variant="ghost" onClick={() => onChange({ ...filters, modifiedAfter: suggestion.before })}>
            {t.preview.leaveThemOut}
          </Button>
        </p>
      )}

      {open === 'types' && (
        <fieldset className="mt-2 rounded-tile border border-rule bg-card-solid/55 px-3.5 py-3">
          <legend className="eyebrow mb-1.5 text-fg-muted">{t.selection.filters.typesLegend}</legend>
          <div className="flex flex-wrap gap-x-4 gap-y-1.5">
            {present.map((kind) => {
              const checked = filters.extensions === null || filters.extensions.includes(kind.extension)
              return (
                <label key={kind.extension} className="flex items-center gap-1.5 text-[12.5px]">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(event) => {
                      const all = present.map((k) => k.extension)
                      const current = filters.extensions ?? all
                      const next = event.target.checked
                        ? [...new Set([...current, kind.extension])]
                        : current.filter((e) => e !== kind.extension)
                      onChange({
                        ...filters,
                        extensions: next.length === all.length || next.length === 0 ? null : next
                      })
                    }}
                  />
                  {kind.extension.replace(/^\./, '') || t.selection.filters.noExtension}{' '}
                  <span className="readout text-fg-subtle">{fmt.formatCount(kind.count)}</span>
                </label>
              )
            })}
          </div>
          <div className="mt-2 flex gap-2">
            <Button size="sm" variant="ghost" onClick={() => onChange({ ...filters, extensions: null })}>
              {t.selection.filters.clear}
            </Button>
          </div>
        </fieldset>
      )}

      {open === 'size' && (
        <fieldset className="mt-2 rounded-tile border border-rule bg-card-solid/55 px-3.5 py-3">
          <legend className="eyebrow mb-1.5 text-fg-muted">{t.selection.filters.sizeLegend}</legend>
          <div className="flex flex-wrap items-center gap-2">
            <div className="w-[110px]">
              <Input
                type="number"
                min={1}
                max={maxMb}
                value={sizeMb}
                onChange={(event) => setSizeMb(event.target.value)}
                aria-label={t.selection.filters.sizeLabel}
              />
            </div>
            <span className="text-[12.5px] text-fg-muted">{t.selection.filters.sizeUnit}</span>
            <Button
              size="sm"
              onClick={() =>
                onChange({ ...filters, maxBytes: Math.max(1, Math.min(maxMb, Number(sizeMb) || maxMb)) * 1024 * 1024 })
              }
            >
              {t.selection.filters.apply}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => onChange({ ...filters, maxBytes: null })}>
              {t.selection.filters.clear}
            </Button>
          </div>
          <p className="mt-1.5 text-[12px] text-fg-subtle">
            {t.selection.filters.sizeHint(fmt.formatBytes(uploadMax))}
          </p>
        </fieldset>
      )}

      {open === 'date' && (
        <fieldset className="mt-2 rounded-tile border border-rule bg-card-solid/55 px-3.5 py-3">
          <legend className="eyebrow mb-1.5 text-fg-muted">{t.selection.filters.dateLegend}</legend>
          <div className="flex flex-wrap items-center gap-2">
            <div className="w-[160px]">
              <Input
                type="date"
                value={date}
                onChange={(event) => setDate(event.target.value)}
                aria-label={t.selection.filters.dateLegend}
              />
            </div>
            <Button
              size="sm"
              onClick={() =>
                onChange({ ...filters, modifiedAfter: date ? new Date(`${date}T00:00:00Z`).toISOString() : null })
              }
            >
              {t.selection.filters.apply}
            </Button>
            {[
              [t.selection.filters.lastYear, 1],
              [t.selection.filters.lastThreeYears, 3]
            ].map(([label, years]) => (
              <Button
                key={String(label)}
                size="sm"
                variant="ghost"
                onClick={() => {
                  const d = new Date()
                  d.setUTCFullYear(d.getUTCFullYear() - Number(years))
                  setDate(d.toISOString().slice(0, 10))
                  onChange({ ...filters, modifiedAfter: d.toISOString() })
                }}
              >
                {label}
              </Button>
            ))}
            <Button size="sm" variant="ghost" onClick={() => onChange({ ...filters, modifiedAfter: null })}>
              {t.selection.filters.anyTimeChoice}
            </Button>
          </div>
        </fieldset>
      )}
    </div>
  )
}
